/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardContentView" />
import * as React from "react";
import { IBacklogLevelConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSProject } from "Tfs/Platform/Page";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format, isGuid } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { IconSize } from "VSSUI/Icon";
import { IListItemDetails } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ZeroData } from "VSSUI/ZeroData";
import { ICardStyle, IClientWorkItem, IObservableClientWorkItem, ParentField } from "Wit/ClientWorkItemService/IClientWorkItem";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { BacklogColumnSettings } from "Wit/Common/BacklogContentService/BacklogContentContants";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { DragMode, IPosition, findElementAtPoint } from "Wit/Common/Controls/Boards/Draggable";
import { OnChangeType } from "Wit/Common/Controls/Boards/EditableField";
import { IInsertionMarkerPosition, InsertionMarker } from "Wit/Common/Controls/Boards/InsertionMarker";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { IPlanningPaneViewService } from "Wit/Common/PlanningPane/Services/PlanningPaneViewService";
import { formatRemainingWorkForDisplay } from "Wit/Common/Utilities/FormatUtils";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { RightPaneViews, TaskboardGroupBy, WorkDetailsSectionType, unparentedId } from "Wit/SprintsHub/Common/Constants";
import {
    ColumnToStateMap,
    ISprintCapacityOptions,
    ISprintTaskboardData,
    ITaskboardWorkItem,
    StateTransitions
} from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { WorkItemArrayEvents } from "Wit/SprintsHub/SprintsView/Components/Taskboard/Contracts/Contracts";
import { ITaskboardRow } from "Wit/SprintsHub/SprintsView/Components/Taskboard/ITaskboardRow";
import { TaskboardRow } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardRow";
import { TaskboardFilterBar } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardFilterBar";
import { TaskboardZeroData } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardZeroData";
import { onWorkItemFormSaveComplete, openWorkItemFormDialog } from "Wit/SprintsHub/SprintsView/Components/Util";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ICapacityService } from "Wit/SprintsHub/SprintsView/Services/ICapacityService";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { ITaskboardShortcutService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardShortcutService";
import { getCardStylesFromCardRules } from "Wit/SprintsHub/SprintsView/Utils/StyleUtils";
import { WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { INamedFieldDataDictionary, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";

const parentLinkType = -2;
const parentLinkTypeName = "System.LinkTypes.Hierarchy-Reverse";
const minParentColumnWidth = 220;
const minColumnWidth = 204;
const taskboardMaxHeight = 250000;
const pageSize = 20;
const sidePanelSelector = "[data-elementtype='side-panel-container']";

export interface ITaskboardContentViewProps {
    filterToggled: IObservableValue<boolean>;
    groupBy: IObservableValue<TaskboardGroupBy>;
    newItemType: IObservableValue<string>;
    selectedIteration: IIterationData | undefined;
    selectedPane: ObservableValue<string>;
    taskboardData: ISprintTaskboardData;
    teamSettings: ITeamSettings;
    workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    onMarkTTI: () => void;
}

export class TaskboardContentView extends TfsComponent<ITaskboardContentViewProps> {
    private filteredTaskboardRows: IObservableArray<ITaskboardRow>;
    private backlogConfiguration: IBacklogLevelConfiguration | undefined;
    private columns: ITableColumn<ITaskboardRow>[] = [];
    private workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] | undefined;
    private taskboardService: ITaskboardService;
    private highlightedElement: HTMLElement | undefined;
    private dragging = false;
    private messages = new ObservableArray<string>([]);
    private newChildId = new ObservableValue<number | undefined>(undefined);
    private previousNewChildId: number | undefined;
    private readonlySelection = new DropdownSelection();
    private columnIdToWorkItemTypeStateMap: ColumnToStateMap = {};
    private cardStyles: ICardStyle[] = [];
    private rowHeights: number[] = [];
    private insertionMarker = new ObservableValue<IInsertionMarkerPosition | undefined>(undefined);
    private workItemModelService: IWorkItemModelService;
    private scrollableContainer = React.createRef<HTMLDivElement>();
    private capacityOptions: ISprintCapacityOptions | undefined;
    private isLoading: boolean = true;
    private tasksBacklogLevelWorkItemTypes: Set<string> = new Set<string>();
    private cardsDragAndDropFixEnabled: boolean;
    private isCardSettingsOptimizationEnabled: boolean;

    public constructor(props: ITaskboardContentViewProps, context: IVssComponentContext) {
        super(props, context);

        this.cardsDragAndDropFixEnabled = !isFeatureFlagEnabled(context.pageContext, FeatureFlags.CardsDragAndDropFixDisabled, false);
        this.isCardSettingsOptimizationEnabled = isFeatureFlagEnabled(context.pageContext, FeatureFlags.ApplySettingsToCardWhenGroupedByAssignedToOptimization, false);

        this.readonlySelection.lock();
        this.taskboardService = context.pageContext.getService<ITaskboardService>("ITaskboardService");
        this.filteredTaskboardRows = this.taskboardService.getFilteredRows();
        this.backlogConfiguration = this.taskboardService.getBacklogConfiguration()?.requirementBacklog;
        this.workItemTypeColorAndIcons = this.taskboardService.getWorkItemTypeColorAndIcons();
        this.cardStyles = getCardStylesFromCardRules(this.props.taskboardData?.boardCardSettings.styles) ?? [];
        this.workItemModelService = context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.capacityOptions = context.pageContext.getService<ICapacityService>("ICapacityService").getCapacityOptions();
        this.initializeTableRows();
    }

    public async componentDidMount() {
        super.componentDidMount();

        if (this.props.teamSettings && this.props.teamSettings.teamId) {
            this.taskboardService.watchTeamTaskboard(this.props.teamSettings.teamId);
        }

        const taskboardShortCutService = this.context.pageContext.getService<ITaskboardShortcutService>("ITaskboardShortcutService");
        taskboardShortCutService.registerShortcuts(() => (this.props.filterToggled.value = !this.props.filterToggled.value));

        const workItemFormDialogService = this.context.pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
        workItemFormDialogService.subscribeToHistoryService(onWorkItemFormSaveComplete);

        this.collapseRowsWithCompletedParents();

        const commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.tasksBacklogLevelWorkItemTypes = await commonBacklogContentService.getWorkItemTypesOfBacklogLevels([BacklogColumnSettings.Tasks]);
    }

    public componentDidUpdate(prevProps: Readonly<ITaskboardContentViewProps>, prevState: Readonly<{}>) {
        super.componentDidUpdate(prevProps, prevState);

        if (prevProps.taskboardData !== this.props.taskboardData) {
            this.initializeTableRows(true);
            const cardStylesFromCardRules = getCardStylesFromCardRules(this.props.taskboardData?.boardCardSettings.styles);
            if (cardStylesFromCardRules) {
                this.cardStyles = cardStylesFromCardRules;
            }
        }

        if (this.props.selectedIteration && prevProps.selectedIteration?.id !== this.props.selectedIteration.id) {
            this.isLoading = true;
            this.taskboardService.setIteration(this.props.selectedIteration);
        }

        if (this.props.teamSettings?.teamId && prevProps.teamSettings?.teamId !== this.props.teamSettings?.teamId) {
            this.taskboardService.watchTeamTaskboard(this.props.teamSettings.teamId);
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        const taskboardShortCutService = this.context.pageContext.getService<ITaskboardShortcutService>("ITaskboardShortcutService");
        taskboardShortCutService.unregisterShortcuts();
    }

    private collapseRowsWithCompletedParents(): void {
        this.taskboardService.getAllRows().value.forEach(row => {
            if (row.parent && this.taskboardService.isStateComplete(row.parent.value)) {
                row.expanded.value = false;
            }
        });
    }

    private async changeAssignedTo(workItem: IObservableClientWorkItem<ITaskboardWorkItem>, identity: WorkItemIdentityRef | undefined) {
        workItem.saving();
        this.onCardChange(this.context.pageContext, workItem.value, CoreFieldRefNames.AssignedTo, identity);
        workItem.saveComplete();
        const messageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messageService.addToast({
            duration: 2000,
            message: Resources.AssignWorkItemToastMessage
        });
    }

    private async changeActivity(workItem: IObservableClientWorkItem<ITaskboardWorkItem>, activity: string | undefined) {
        workItem.saving();
        const activityFieldReferenceName = this.capacityOptions?.activityFieldReferenceName ?? "Microsoft.VSTS.Common.Activity";
        this.onCardChange(this.context.pageContext, workItem.value, activityFieldReferenceName, activity);
        workItem.saveComplete();
        const messageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messageService.addToast({
            duration: 2000,
            message: Resources.ChangeWorkItemActivityToastMessage
        });
    }

    private async changeIteration(workItem: IObservableClientWorkItem<ITaskboardWorkItem>, iterationPath: string, rowId: string | undefined) {
        let itemsMoved = 1;
        workItem.saving();
        try {
            if (rowId) {
                const row = this.taskboardService.getTaskboardRowMap().get(rowId);
                if (row?.parent && row.parent === workItem) {
                    const children = [...row.children.value];
                    const notCompleted = children.filter(child => !this.taskboardService.isStateComplete(child.value));
                    for (const child of notCompleted) {
                        await this.onCardChange(this.context.pageContext, child.value, CoreFieldRefNames.IterationPath, iterationPath);
                        itemsMoved++;
                    }
                }
            }
            await this.onCardChange(this.context.pageContext, workItem.value, CoreFieldRefNames.IterationPath, iterationPath);
        } finally {
            workItem.saveComplete();
        }
        const messageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        messageService.addToast({
            duration: 2000,
            message: format(Resources.MoveToIterationToastMessage, itemsMoved)
        });

        const planningService = this.context.pageContext.getService<IPlanningPaneViewService>("IPlanningPaneViewService");
        planningService.updateIterations();
    }

    private getColumnStateMap(): ColumnToStateMap {
        const columnStateMap = this.props.taskboardData.taskboardModel.columnIdToWorkItemTypeStateMap;
        if (columnStateMap && Object.keys(columnStateMap).length !== 0) {
            return columnStateMap;
        }

        const transitions = this.props.taskboardData.taskboardModel.transitions;
        if (transitions) {
            return this.buildColumnToStateMap(transitions);
        }

        return columnStateMap;
    }

    private resetGroupByView = () => {
        // Re-filter the items with new group by
        this.updateTaskboard(new Set(this.taskboardService.getFilterManager().filter(this.context.pageContext)));
        this.setMessages();
        this.initializeTableColumns();
        this.taskboardService.setSomeRowsExpanded(true);
        return true;
    };

    private initializeTableRows = (forceRefresh?: boolean) => {
        this.messages.value = [];

        if (this.props.taskboardData) {
            this.columnIdToWorkItemTypeStateMap = this.getColumnStateMap();
            this.taskboardService.initializeRows(forceRefresh);
            this.setMessages();
        }
    };

    private updateTaskboard = (filteredWorkItemIds: Set<number>) => {
        let allRows = this.taskboardService.getAllRows().value;
        if (this.taskboardService.getFilterManager().isFiltering()) {
            allRows = allRows.filter(item => {
                if (item.rowType !== this.props.groupBy.value) {
                    return false;
                }
                let showRow = item.parent && filteredWorkItemIds.has(item.parent.value.id);
                item.children.value.forEach(child => {
                    const filteredOut = !filteredWorkItemIds.has(child.value.id);
                    child.value.filteredOut.value = filteredOut;
                    if (!filteredOut) {
                        showRow = true;
                    }
                });
                return showRow;
            });
        } else {
            allRows = allRows.filter(row => row.rowType === this.props.groupBy.value);
            this.taskboardService.getAllItems().forEach(item => (item.value.filteredOut.value = false));
        }
        if (this.props.groupBy.value === TaskboardGroupBy.AssignedTo) {
            allRows = allRows.sort((aggr1, aggr2) => {
                const name1 = aggr1.assignedTo?.displayName;
                const name2 = aggr2.assignedTo?.displayName;
                // Added undefined and null checks so that Unassigned WI sorted down correctly
                if (!name1 && !name2) {
                    return 0;
                }
                if (!name1) {
                    return -1;
                }
                if (!name2) {
                    return 1;
                }
                return name1.localeCompare(name2, navigator.language);
            });
        }
        this.filteredTaskboardRows.value = allRows;
        this.taskboardService.resetColumnIdToRemainingWorkMapValues();
    };

    private getTableColumns = (): ITableColumn<ITaskboardRow>[] => {
        if (!this.props.taskboardData) {
            return [];
        }

        if (!this.columns.length) {
            this.initializeTableColumns();
        }

        return this.columns;
    };

    private initializeTableColumns = () => {
        const parentColumn = {
            id: "parent-column",
            width: minParentColumnWidth,
            renderCell: (rowIndex: number, columnIndex: number) => {
                return <td key={columnIndex} />;
            },
            renderHeaderCell: () => {
                return (
                    <th className="text-left" key="parent-header-cell">
                        <Observer someRowsExpanded={this.taskboardService.getSomeRowsExpanded()}>
                            {(props: { someRowsExpanded: boolean }) => (
                                <Button
                                    subtle
                                    text={props.someRowsExpanded ? Resources.CollapseAll : Resources.ExpandAll}
                                    iconProps={{ iconName: props.someRowsExpanded ? "DoubleChevronUp" : "DoubleChevronDown", size: IconSize.small }}
                                    onClick={() => {
                                        props.someRowsExpanded ? this.collapseAll() : this.expandAll();
                                    }}
                                />
                            )}
                        </Observer>
                    </th>
                );
            }
        };
        const columns = this.props.taskboardData!.taskboardModel.columns;
        let stateColumns: ITableColumn<ITaskboardRow>[] = [];
        const columnIdToRemainingWorkMap = this.taskboardService.getRemainingWorkMap();
        if (columns && columns.length) {
            stateColumns = columns.map(column => {
                if (!columnIdToRemainingWorkMap.has(column.id)) {
                    columnIdToRemainingWorkMap.set(column.id, new ObservableValue<number>(0));
                }
                return {
                    id: column.id,
                    name: column.name,
                    minWidth: minColumnWidth,
                    width: -100 / this.props.taskboardData!.taskboardModel.columns.length,
                    renderCell: (rowIndex: number, columnIndex: number) => {
                        return <td key={columnIndex} />;
                    },
                    renderHeaderCell: () => (
                        <TaskboardHeader
                            key={`${column.name}-column-header`}
                            name={column.name}
                            remainingWork={columnIdToRemainingWorkMap.get(column.id)!}
                        />
                    )
                };
            });
        } else {
            stateColumns = this.getDefaultColumns();
            this.columnIdToWorkItemTypeStateMap = this.getColumnStateMap();
        }

        this.columns = [parentColumn, ...stateColumns];
    };

    private getDefaultColumns = () => {
        return this.props.taskboardData!.taskboardModel.states.map((state, index) => {
            const columnId = state.toLocaleLowerCase();
            const columnIdToRemainingWorkMap = this.taskboardService.getRemainingWorkMap();
            if (!columnIdToRemainingWorkMap.has(columnId)) {
                columnIdToRemainingWorkMap.set(columnId, new ObservableValue<number>(0));
            }
            return {
                id: columnId,
                width: -100 / this.props.taskboardData!.taskboardModel.states.length,
                minWidth: minColumnWidth,
                name: state,
                renderCell: (rowIndex: number, columnIndex: number) => {
                    return <td key={columnIndex} />;
                },
                renderHeaderCell: (columnIndex: number) => (
                    <TaskboardHeader key={`${state}-column-header`} name={state} remainingWork={columnIdToRemainingWorkMap.get(columnId)!} />
                )
            };
        });
    };

    private buildColumnToStateMap(transitions: StateTransitions): ColumnToStateMap {
        const columnToStateMap: ColumnToStateMap = {};
        for (const workItemType in transitions) {
            for (const state in transitions[workItemType]) {
                const columnId = state.toLocaleLowerCase();
                if (!columnToStateMap[columnId]) {
                    columnToStateMap[columnId] = {};
                }
                columnToStateMap[columnId][workItemType] = state;
            }
        }
        return columnToStateMap;
    }

    private collapseAll = () => {
        this.taskboardService.setSomeRowsExpanded(false);
        this.taskboardService.getAllRows().value.forEach(row => {
            row.expanded.value = false;
        });
    };

    private expandAll = () => {
        this.taskboardService.setSomeRowsExpanded(true);
        this.taskboardService.getAllRows().value.forEach(row => {
            row.expanded.value = true;
        });
    };

    private renderTaskboardRow = (rowIndex: number, item: ITaskboardRow, details: IListItemDetails<ITaskboardRow>) => {
        return (
            <TaskboardRow
                details={details}
                rowIndex={rowIndex}
                rowHeights={this.rowHeights}
                item={item}
                cardStyles={this.cardStyles}
                taskboardData={this.props.taskboardData}
                onCardChange={this.onCardChange}
                onCardClick={this.onCardClick}
                onCardDrag={this.onCardDrag}
                onChildCardKeyDown={this.onChildCardKeyDown}
                onParentCardKeyDown={this.onCardKeyDown}
                onParentItemCreate={this.onParentItemCreate}
                onParentItemCancel={this.onParentItemCancel}
                onChildItemCreate={this.onChildItemCreate}
                onChildItemCancel={this.onChildItemCancel}
                orderByAssignedTo={this.props.groupBy.value === TaskboardGroupBy.AssignedTo}
                scrollableData={{
                    scrollableContainer: this.scrollableContainer,
                    scrollableSettings: { 
                        debounceTime: 20, 
                        forbiddenAreasSelectors: this.cardsDragAndDropFixEnabled ? sidePanelSelector : undefined 
                    }
                }}
                someRowsExpanded={this.taskboardService.getSomeRowsExpanded()}
                columns={this.getTableColumns()}
                workItemTypeColorAndIcons={this.workItemTypeColorAndIcons}
                filteredTaskboardRows={this.filteredTaskboardRows}
                newItemType={this.props.newItemType}
                newChildId={this.newChildId}
                onNewChildSelected={this.onNewChildSelected}
                previousNewChildId={this.previousNewChildId}
                workItemTrackingDataProvider={this.props.workItemTrackingDataProvider}
            />
        );
    };

    private moveChild = async (
        rowId: string | undefined,
        startingRowId: string,
        columnId: string | undefined,
        startingColumnId: string,
        itemId: number,
        columnName: string,
        workItem: IObservableClientWorkItem<ITaskboardWorkItem>
    ) => {
        try {
            workItem.saving();
            if (columnId && columnId !== startingColumnId) {
                this.updateColumn(itemId, startingColumnId, columnId, workItem);
            }
            if (rowId && rowId !== startingRowId) {
                await this.updateRow(itemId, startingRowId, rowId);
            }

            if (await workItem.value.isValid(this.context.pageContext)) {
                await workItem.value.save(this.context.pageContext);
            }

            // Update the workItem column asynchronously if column is changed
            if (columnId && columnId !== startingColumnId && this.props.taskboardData!.taskboardModel.areColumnsCustomized) {
                this.updateWorkItemColumn(columnName, itemId);
            }
        } finally {
            workItem.saveComplete();

            const taskboardRows = [
                this.taskboardService.getTaskboardRowMap().get(startingRowId),
                this.taskboardService.getTaskboardRowMap().get(rowId ?? "")
            ];
            // We need to force reloading of parent work item the next time it is opened to have an up to date list of links
            taskboardRows.forEach(taskboardRow => {
                const parentWorkItemId = taskboardRow?.parent?.value.id;
                if (parentWorkItemId) {
                    this.workItemModelService.clearWorkItem(parentWorkItemId);
                }
            });
        }
    };

    private onNewChildSelected = (type: string, row: ITaskboardRow) => {
        const fieldMap: INamedFieldDataDictionary = {};
        const state = Object.keys(this.props.taskboardData?.taskboardModel.taskWorkItemTypesToStateMap[type] as {})[0];
        fieldMap[CoreFieldRefNames.WorkItemType] = type;
        fieldMap[CoreFieldRefNames.State] = state;
        fieldMap[CoreFieldRefNames.TeamProject] = getTFSProject(this.context.pageContext)?.name;
        if (row.parent) {
            fieldMap[CoreFieldRefNames.Parent] = row.parent.value.id;
            fieldMap[ParentField.WorkItemType] = row.parent.value.getFieldValue(CoreFieldRefNames.WorkItemType);
            fieldMap[ParentField.Title] = row.parent.value.getFieldValue(CoreFieldRefNames.Title);
        }
        const childItem = this.taskboardService.initializeNewItem(fieldMap, row.parent?.value.id ?? unparentedId);
        childItem.value.setFieldValue(CoreFieldRefNames.IterationId, this.props.selectedIteration?.nodeId);
        childItem.value.setFieldValue(CoreFieldRefNames.IterationPath, this.props.selectedIteration?.friendlyPath);
        this.newChildId.value = childItem.value.id;
        this.previousNewChildId = childItem.value.id;
    };

    private onParentItemCreate = (id: number) => {
        const row = this.taskboardService.getAllRows().value.find(row => row.parent?.value.id === id);
        if (row) {
            this.taskboardService.getTaskboardRowMap().set(String(id), row);
            this.props.newItemType.value = "";
        }
    };

    private onParentItemCancel = () => {
        this.taskboardService.getAllRows().splice(0, 1);
        this.taskboardService.getFilteredRows().splice(0, 1);
        this.props.newItemType.value = "";
    };

    private onChildItemCreate = async (
        item: ITaskboardRow,
        child: IObservableClientWorkItem<ITaskboardWorkItem>,
        originalId: number,
        enterPressed: boolean
    ) => {
        this.newChildId.value = undefined;
        this.taskboardService.actualizeChildItem(child, originalId);

        if (item.parent) {
            if (item.parent.value.getFieldValue<string>(CoreFieldRefNames.IterationId) === this.props.selectedIteration?.nodeId) {
                const fieldRefNamesFromParent = [
                    CoreFieldRefNames.IterationId,
                    CoreFieldRefNames.IterationPath,
                    CoreFieldRefNames.AreaId,
                    CoreFieldRefNames.AreaPath
                ];

                fieldRefNamesFromParent.map((fieldRefName: string) => {
                    const parentFieldValue = item.parent?.value.getFieldValue<string>(fieldRefName);
                    if (parentFieldValue !== undefined) {
                        child.value.setFieldValue(fieldRefName, parentFieldValue);
                    }
                });
            }

            await this.updateWorkItemRow(child, item.parent.value.id);
            await child.value.save(this.context.pageContext);
            child.notify();

            //after adding child task we need to force reloading of the parent work item.
            //in this case, the list of linked workitems will be correctly updated when the User opens this parent workitem in the dialog
            this.workItemModelService.clearWorkItem(item.parent.value.id);
        }
        if (enterPressed) {
            const type = child.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType) || "";
            this.onNewChildSelected(type, item);
        }
    };

    private onChildItemCancel = async (child: IObservableClientWorkItem<IClientWorkItem>) => {
        await this.taskboardService.removeWorkItem(child.value.id);
        this.newChildId.value = undefined;
        this.previousNewChildId = undefined;
    };

    private highlightOnDragOver(cell: HTMLElement | undefined): void {
        if (cell) {
            this.highlightedElement?.classList.remove("highlighted");
            cell?.classList.add("highlighted");
            this.highlightedElement = cell;
        } else {
            this.highlightedElement?.classList.remove("highlighted");
            this.highlightedElement = undefined;
        }
    }

    private onCardDrag = (
        e: PointerEvent,
        card: HTMLElement,
        mode: DragMode,
        position: IPosition,
        workItem: IObservableClientWorkItem<ITaskboardWorkItem>,
        startingColumnId: string,
        startingRowId: string,
        isParent?: boolean
    ): undefined => {
        const cell = findElementAtPoint(".taskboard-expanded-cell, .taskboard-collapsed-row, .taskboard-parent-cell", e.pageX, e.pageY);
        const sidePanelElement = findElementAtPoint(sidePanelSelector, e.pageX, e.pageY);
        const columnId = cell?.dataset.iscollapsed ? startingColumnId : cell?.dataset.column;
        const rowId = cell?.dataset.row;
        const itemId = Number(card.dataset.itemid);
        const hoveredCard = cell && findElementAtPoint(".taskboard-card", e.pageX, e.pageY);
        const hoveredCardId = Number(hoveredCard?.dataset.itemid);
        const row = this.taskboardService.getTaskboardRowMap().get(startingRowId);
        const workItemColumnIdMap = this.taskboardService.getWorkItemColumnMap().value;
        const children = row?.children.value.filter(child => workItemColumnIdMap[child.value?.id] === workItemColumnIdMap[workItem.value.id]);

        // if we're in the bottom half of the row, insert below the row
        const hoveredRect = hoveredCard?.getBoundingClientRect();
        const below = hoveredRect && e.pageY > hoveredRect.top + window.scrollY + hoveredRect.height / 2;
        const canReorder = this.isReorderSupported();
        const workitemTypeName = workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
        const workitemTypeAllowedState = columnId && workitemTypeName ? this.columnIdToWorkItemTypeStateMap[columnId][workitemTypeName] : undefined;

        if (e.type === "pointermove") {
            this.dragging = true;
            if ((!sidePanelElement || !this.cardsDragAndDropFixEnabled) && cell !== this.highlightedElement && !isParent) {
                if (columnId === startingColumnId && rowId === startingRowId) {
                    this.highlightOnDragOver(undefined);
                } else {
                    if (workitemTypeAllowedState) {
                        cell?.classList.add("highlighted");
                    }
                    this.highlightedElement?.classList.remove("highlighted");
                    this.highlightedElement = cell;
                }
            }

            if (!cell || (this.cardsDragAndDropFixEnabled && sidePanelElement)) {
                if (this.props.selectedPane.value === RightPaneViews.Planning) {
                    this.highlightOnDragOver(findElementAtPoint(".planning-sprint-card", e.pageX, e.pageY));
                } else if (this.props.selectedPane.value === RightPaneViews.WorkDetails) {
                    const element = findElementAtPoint(".work-details-pane-list-item", e.pageX, e.pageY);
                    if (
                        element &&
                        [WorkDetailsSectionType.ASSIGNED_TO, WorkDetailsSectionType.ACTIVITY].some(
                            type => type === element.getAttribute("data-workdetailspanetype")
                        )
                    ) {
                        const workItemType = workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
                        const workDetailsSectionType = element.getAttribute("data-workdetailspanetype");
                        if (workItemType && workDetailsSectionType === WorkDetailsSectionType.ACTIVITY) {
                            const isTaskOrRequirement = this.tasksBacklogLevelWorkItemTypes.has(workItemType);
                            if (isTaskOrRequirement) {
                                this.highlightOnDragOver(element);
                            }
                        } else {
                            this.highlightOnDragOver(element);
                        }
                    } else if (this.cardsDragAndDropFixEnabled) {
                        this.highlightOnDragOver(undefined);
                    }
                }
            }

            if (canReorder) {
                this.updateReorderMarker(
                    itemId,
                    hoveredCard,
                    hoveredCardId,
                    below,
                    rowId,
                    startingRowId,
                    columnId,
                    startingColumnId,
                    this.props.groupBy.value
                );
            }
        } else if (e.type === "pointerup") {
            window.setTimeout(() => {
                this.dragging = false;
                this.clearReorderMarker();
            }, 20);
            const element = this.highlightedElement?.querySelector("[data-iterationpath]") as HTMLElement;

            if (element?.dataset.iterationpath) {
                // move item to that iteration
                const iterationPath = element.dataset.iterationpath;
                if (iterationPath) {
                    this.changeIteration(workItem, iterationPath, isParent ? startingRowId : undefined);
                }
            }

            if (this.highlightedElement) {
                const workDetailsSectionType = this.highlightedElement.getAttribute("data-workdetailspanetype") as string | null;
                if (workDetailsSectionType === WorkDetailsSectionType.ASSIGNED_TO) {
                    const identityId = this.highlightedElement.getAttribute("data-identityid");
                    const identityIdIsGuid = isGuid(identityId ?? "");
                    if (identityIdIsGuid) {
                        const workItemIdentityRefRaw = this.highlightedElement.getAttribute("data-workitemidentityref");
                        if (workItemIdentityRefRaw !== null) {
                            const workItemIdentityRef: WorkItemIdentityRef = JSON.parse(workItemIdentityRefRaw);
                            this.changeAssignedTo(workItem, workItemIdentityRef);
                        }
                    } else {
                        this.changeAssignedTo(workItem, undefined);
                    }
                }
                if (workDetailsSectionType === WorkDetailsSectionType.ACTIVITY) {
                    const activityId = this.highlightedElement.getAttribute("data-activityid");
                    if (activityId) {
                        this.changeActivity(workItem, activityId === Resources.Unassigned ? undefined : activityId);
                    }
                }
            }

            if (cell?.dataset.iscollapsed && rowId) {
                const row = this.taskboardService.getTaskboardRowMap().get(rowId);
                if (row) {
                    row.expanded.value = true;
                }
            }
            this.highlightedElement?.classList.remove("highlighted");

            if (isParent) {
                e.preventDefault();
                return;
            }

            if ((rowId && rowId !== startingRowId) || (columnId && columnId !== startingColumnId)) {
                const columnName = cell?.dataset.columnname || "";
                if (workitemTypeAllowedState) {
                    this.moveChild(rowId, startingRowId, columnId, startingColumnId, itemId, columnName, workItem);
                }
            }

            if (rowId === startingRowId && columnId === startingColumnId && this.props.groupBy.value === TaskboardGroupBy.Parents) {
                // reorder within cell
                if (row && children && hoveredCardId !== itemId) {
                    const index = children.findIndex(child => child.value.id === workItem.value.id);
                    const [previousId, nextId] = canReorder ? this.getColumnPlacementIndexes(children, hoveredCardId, below) : [undefined, undefined];
                    if (previousId !== itemId && nextId !== itemId) {
                        this.reorderItem(row, workItem, index, previousId, nextId, children);
                    }
                }
            }
        }
        e.preventDefault();
        return;
    };

    private isReorderSupported(): boolean {
        return this.taskboardService.hasAgileToolsBacklogManagementPermission() && !this.props.taskboardData.taskboardModel.hasNestedTasks;
    }

    private updateReorderMarker(
        draggedItemId: number,
        closestItem?: HTMLElement,
        closestItemId?: number,
        below?: boolean,
        rowId?: string,
        startingRowId?: string,
        columnId?: string,
        startingColumnId?: string,
        groupBy?: TaskboardGroupBy
    ) {
        if (
            closestItem &&
            closestItemId &&
            closestItemId !== draggedItemId &&
            rowId === startingRowId &&
            columnId === startingColumnId &&
            this.props.groupBy.value === TaskboardGroupBy.Parents
        ) {
            // calculate the insertion marker location
            const closestRect = closestItem.getBoundingClientRect();
            this.insertionMarker.value = {
                // margin between cards is 12px, insertion marker is 2px
                // so we want to orient it 5px down from the upper bound
                top: below ? closestRect.bottom + 5 : closestRect.top - 7,
                left: closestRect.left,
                width: closestRect.width
            };
        } else {
            // no marker on the board given the current pointer position, so clear it
            this.clearReorderMarker();
        }
    }

    private clearReorderMarker() {
        this.insertionMarker.value = undefined;
    }

    private getColumnPlacementIndexes(
        columnWorkItems: IObservableClientWorkItem<ITaskboardWorkItem>[],
        closestItemId?: number,
        below?: boolean
    ): [number | undefined, number | undefined] {
        if (!closestItemId) {
            return [undefined, undefined];
        }

        const closestItemIndex = columnWorkItems.findIndex(item => item.value.id === closestItemId);
        const nextClosestItemIndex = closestItemIndex + (below ? 1 : -1);
        let previousId: number | undefined;
        let nextId: number | undefined;
        if (below) {
            previousId = closestItemId;
            nextId = nextClosestItemIndex < columnWorkItems.length ? columnWorkItems[nextClosestItemIndex].value.id : undefined;
        } else {
            previousId = nextClosestItemIndex > -1 ? columnWorkItems[nextClosestItemIndex].value.id : undefined;
            nextId = closestItemId;
        }

        return [previousId, nextId];
    }

    private onCardKeyDown = (
        event: React.KeyboardEvent<HTMLElement>,
        item: IObservableClientWorkItem<IClientWorkItem>,
        row: ITaskboardRow,
        rowIndex: number
    ) => {
        // A lot of this comes straight from the old view
        const element = event.target as HTMLElement;
        if (
            element &&
            (equals(element.tagName, "INPUT", true) ||
                equals(element.tagName, "SELECT", true) ||
                equals(element.tagName, "TEXTAREA", true) ||
                element.isContentEditable)
        ) {
            return;
        }

        if (event.key === "Enter") {
            this.onCardClick(item.value.id);
        }
        if (event.key === "Delete") {
            const hotKeyEventService = this.context.pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
            hotKeyEventService.fireEvent(
                { workItemId: item.value.id, projectName: item.value.getFieldValue(CoreFieldRefNames.TeamProject) },
                IHotKeyEventType.WorkItemDelete
            );
        }
        // Focus on new item button in current row if only C is pressed.  If Ctrl+C is pressed the user is trying to copy.
        else if ((event.key === "c" || event.key === "C") && !event.ctrlKey) {
            const childTypes = this.props.taskboardData?.taskboardModel.childWorkItemTypes;
            if (childTypes && childTypes.length > 0) {
                this.onNewChildSelected(childTypes[0], row);
            }
        }
        // Focus on expand collapse item
        else if ((event.key === "PageDown" || event.key === "PageUp") && event.shiftKey) {
            const rows = document.getElementsByClassName("taskboard-expand-collapse-button");
            const modifier = event.key === "PageDown" ? +1 : -1;
            const nextRowExpandCollapseButton = rows[rowIndex + modifier] as HTMLElement;
            nextRowExpandCollapseButton.focus();
        }
    };

    private onChildCardKeyDown = async (
        event: React.KeyboardEvent<HTMLElement>,
        item: IObservableClientWorkItem<ITaskboardWorkItem>,
        row: ITaskboardRow,
        rowIndex: number
    ) => {
        this.onCardKeyDown(event, item, row, rowIndex);
        const workItemId = item.value.id;

        if (workItemId <= 0) {
            // block moving not saved items
            return;
        }

        const workItemColumnIdMap = this.taskboardService.getWorkItemColumnMap().value;

        // Update state, move columns
        if (
            (event.key === "ArrowRight" || event.key === "ArrowLeft" || event.key === "Right" || event.key === "Left") &&
            (event.ctrlKey || event.metaKey)
        ) {
            const startingColumn = workItemColumnIdMap[item.value.id];
            const modifier = event.key === "ArrowRight" || event.key === "Right" ? +1 : -1;
            const startingIndex = this.columns.findIndex(column => column.id === startingColumn);
            const nextIndex = startingIndex + modifier;

            // Columns includes parent column - we can only move between indexes 1 & length - 1
            if (nextIndex > 0 && nextIndex < this.columns.length) {
                item.saving();
                try {
                    const nextColumn = this.columns[nextIndex];

                    // Update the UI, then update the workItem asynchronously.
                    this.updateColumn(workItemId, startingColumn, nextColumn.id, item);
                    if (await item.value.isValid(this.context.pageContext)) {
                        await item.value.save(this.context.pageContext);

                        if (this.props.taskboardData!.taskboardModel.areColumnsCustomized) {
                            await this.updateWorkItemColumn(nextColumn.name ?? "", workItemId);
                        }
                    }
                } finally {
                    item.saveComplete();
                    this.focusCard(workItemId);
                }
            }
        } else if (
            (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "Up" || event.key === "Down") &&
            (event.ctrlKey || event.metaKey)
        ) {
            if (!event.shiftKey) {
                // Reorder, move within cell
                let previousId: number | undefined = undefined;
                let nextId: number | undefined = undefined;

                const children = row.children.value.filter(child => workItemColumnIdMap[child.value?.id] === workItemColumnIdMap[item.value.id]);
                const indexOfItem = children.findIndex(child => child.value.id === item.value.id);
                if ((event.key === "ArrowUp" || event.key === "Up") && indexOfItem > 0) {
                    // Go up two items to get previous id, up one item to get next id
                    previousId = indexOfItem - 2 > -1 ? children[indexOfItem - 2].value?.id : undefined;
                    nextId = children[indexOfItem - 1].value.id;
                } else if ((event.key === "ArrowDown" || event.key === "Down") && indexOfItem > -1 && indexOfItem < children.length - 1) {
                    // Go down two items to get next id, down one item to get previous id
                    nextId = indexOfItem + 2 < children.length ? children[indexOfItem + 2].value.id : undefined;
                    previousId = children[indexOfItem + 1].value.id;
                }

                if (previousId || nextId) {
                    this.reorderItem(row, item, indexOfItem, previousId, nextId, children);
                }
            } else {
                // Reparent, move to next row
                if (this.props.groupBy.value === TaskboardGroupBy.Parents) {
                    const currentRowId = this.filteredTaskboardRows.value[rowIndex].parent?.value.id ?? unparentedId;
                    const modifier = event.key === "Down" || event.key === "ArrowDown" ? +1 : -1;
                    const nextRowId = this.filteredTaskboardRows.value[rowIndex + modifier].parent?.value.id ?? unparentedId;

                    item.saving();
                    try {
                        await this.updateRow(item.value.id, String(currentRowId), String(nextRowId));
                        if (await item.value.isValid(this.context.pageContext)) {
                            await item.value.save(this.context.pageContext);
                        }
                        item.notify();
                    } finally {
                        item.saveComplete();
                        this.focusCard(workItemId);
                    }
                }
            }
        }
    };

    private focusCard(itemId: number): void {
        const cardElement = document.querySelector(`.taskboard-card[data-itemid="${itemId}"]`) as HTMLElement | null;
        if (cardElement) {
            cardElement.focus();
        }
    }

    private reorderItem(
        row: ITaskboardRow,
        item: IObservableClientWorkItem<ITaskboardWorkItem>,
        indexOfItem: number,
        previousId: number | undefined,
        nextId: number | undefined,
        children: IObservableClientWorkItem<ITaskboardWorkItem>[]
    ) {
        let modifiedChildren = [...children];
        modifiedChildren.splice(indexOfItem, 1);
        if (!previousId) {
            // insert item @start of children
            modifiedChildren.unshift(item);
        } else if (nextId) {
            // insert item after previous item
            const prevIndex = modifiedChildren.findIndex(child => child.value.id === previousId);
            modifiedChildren.splice(prevIndex + 1, 0, item);
        } else {
            // insert item as last item
            modifiedChildren = modifiedChildren.concat(item);
        }

        const workItemColumnIdMap = this.taskboardService.getWorkItemColumnMap().value;
        const unchangedChildren = row.children.value.filter(child => workItemColumnIdMap[child.value?.id] !== workItemColumnIdMap[item.value.id]);
        const parentId = row.parent?.value.id;
        const iteration = this.taskboardService.selectedIteration;

        const reorderRequired = previousId || nextId;

        if (reorderRequired) {
            row.children.value = [...modifiedChildren, ...unchangedChildren];

            this.taskboardService.reorderWorkItems(
                item.value.id,
                this.projectData?.id ?? "",
                this.teamData?.id ?? "",
                previousId,
                nextId,
                iteration.friendlyPath,
                {
                    onFailure: () => {
                        row.children.value = [...children, ...unchangedChildren];
                    },
                    onSuccess: result => {
                        const orderByField = this.props.taskboardData!.taskboardModel.orderByField;
                        item.value.setFieldValue(orderByField, result[0].order);
                        children = modifiedChildren;
                    }
                },
                undefined,
                undefined,
                iteration.id,
                parentId
            );
        }
    }

    private setMessages() {
        this.messages.removeAll();
        if (this.props.taskboardData?.taskboardModel.hasNestedTasks) {
            let message = Resources.SameCategoryHierarchy;
            const missingWorkItemIds = this.props.taskboardData.taskboardModel.missingWorkItemIds;
            const problemWorkItemIds = this.props.taskboardData.taskboardModel.reorderBlockingWorkItemIds;
            if (missingWorkItemIds && missingWorkItemIds.length) {
                message += "\r\n" + format(Resources.FeaturesBlockedWithWorkItems, missingWorkItemIds.join(", "));
            }
            if (problemWorkItemIds && problemWorkItemIds.length) {
                message += "\r\n" + format(Resources.WorkItemsToFix, problemWorkItemIds.join(", "));
            }
            this.messages.push(message);
        }
    }

    // Update the UI, then update the workItem asynchronously.
    private updateRow = async (itemId: number, startingRowId: string, rowId: string) => {
        const startingRow = this.taskboardService.getTaskboardRowMap().get(startingRowId);
        const childIndex = startingRow!.children.value.findIndex(child => child.value.id === itemId);
        if (childIndex > -1) {
            const child = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.CardMovementOptimization)
                ? startingRow!.children.spliceWithCustomAction(childIndex, 1, WorkItemArrayEvents.RemoveWorkItem)[0]
                : startingRow!.children.splice(childIndex, 1)[0];
            const newParent = this.taskboardService.getTaskboardRowMap().get(rowId);
            if (newParent) {
                newParent!.children.push(child);
                await this.updateWorkItemRow(
                    child,
                    newParent?.parent?.value.id,
                    newParent?.assignedTo,
                    newParent?.parent?.value.getFieldValue(CoreFieldRefNames.WorkItemType),
                    newParent?.parent?.value.getFieldValue(CoreFieldRefNames.Title)
                );
            }
        }
    };

    // Update the workItem asynchronously.
    private updateWorkItemRow = async (
        child: IObservableClientWorkItem<IClientWorkItem>,
        newParentId: number | undefined,
        newAssignedTo?: IdentityRef | undefined,
        newParentType?: string | null,
        newParentTitle?: string | null
    ) => {
        await child.value.hydrate(this.context.pageContext);
        const workItem = child.value.iWorkItem.value!;
        const existingParentLink =
            this.taskboardService.groupBy.value === TaskboardGroupBy.Parents &&
            workItem.getLinks().find(link => link.linkData.LinkType === parentLinkType);
        if (existingParentLink) {
            workItem.removeLinks([existingParentLink]);
        }

        if (newParentId) {
            workItem.addLinks([await WorkItemLink.create(this.context.pageContext, parentLinkTypeName, newParentId)]);
        }

        if (newAssignedTo) {
            workItem.setFieldValueByName(CoreFieldRefNames.AssignedTo, newAssignedTo.displayName);
        }

        if (newParentType) {
            child.value.setFieldValue(ParentField.WorkItemType, newParentType);
        }

        if (newParentTitle) {
            child.value.setFieldValue(ParentField.Title, newParentTitle);
        }

        child.updateBackgroundColor(this.context.pageContext, this.cardStyles);
    };

    private updateColumn = (itemId: number, startingColumnId: string, columnId: string, workItem: IObservableClientWorkItem<IClientWorkItem>) => {
        const stateMap = this.columnIdToWorkItemTypeStateMap;
        const workItemColumnIdMap = this.taskboardService.getWorkItemColumnMap().value;
        workItemColumnIdMap[itemId] = columnId;
        // Let subscribers know the value changed.
        this.taskboardService.getWorkItemColumnMap().value = workItemColumnIdMap;

        // Update the columns' remaining work
        const remainingWork = workItem.value.getFieldValue<number>(this.props.taskboardData!.taskboardModel.workRollupField);
        if (remainingWork) {
            const columnIdToRemainingWorkMap = this.taskboardService.getRemainingWorkMap();
            columnIdToRemainingWorkMap.get(startingColumnId)!.value -= remainingWork;
            columnIdToRemainingWorkMap.get(columnId)!.value += remainingWork;
        }

        const workItemType = workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
        if (workItemType && stateMap[columnId] !== stateMap[startingColumnId]) {
            workItem.value.setFieldValue(CoreFieldRefNames.State, stateMap[columnId][workItemType]);
        }
    };

    private updateWorkItemColumn = async (columnName: string, itemId: number) => {
        const workClient = getWorkClient(this.context.pageContext);
        try {
            await workClient.updateWorkItemColumn(
                { newColumn: columnName },
                this.props.teamSettings as any as TeamContext,
                this.props.taskboardData?.iterationId ?? "",
                itemId
            );
        } catch (error) {
            if (error.message) {
                this.messages.push(error.message);
            }
        }
    };

    private onCardChange: OnChangeType = async (pageContext, item, fieldReferenceName, newValue) => {
        const error = await this.taskboardService.updateItem(item.id, fieldReferenceName, newValue);
        if (error) {
            this.messages.push(error);
        }
    };

    private onCardClick = (workItemId: number) => {
        if (!this.dragging) {
            openWorkItemFormDialog(this.context.pageContext, workItemId);
        }
    };

    private onNewItemTypeChanged = async () => {
        if (this.props.newItemType.value) {
            const projectId = getTFSProject(this.context.pageContext)?.id ?? "";
            const workItemTypeService = this.context.pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");

            const workItemTypeDataList = await workItemTypeService.getWorkItemTypeData(projectId, [this.props.newItemType.value]);
            if (!workItemTypeDataList) {
                return;
            }
            const workItemTypeData: IWorkItemTypeData = workItemTypeDataList[0];
            const fieldMap: INamedFieldDataDictionary = {};
            fieldMap[CoreFieldRefNames.WorkItemType] = this.props.newItemType.value;
            fieldMap[CoreFieldRefNames.State] = workItemTypeData.orderedStates
                ? workItemTypeData.orderedStates[0]
                : this.props.taskboardData?.taskboardModel.states[0];
            fieldMap[CoreFieldRefNames.TeamProject] = getTFSProject(this.context.pageContext)?.name;
            this.taskboardService.initializeNewItem(fieldMap, undefined);
        }
    };

    private renderTaskboard(columns: ITableColumn<ITaskboardRow>[]): JSX.Element {
        return (
            <Table<ITaskboardRow>
                columns={columns}
                containerClassName="sticky-table full-height full-width absolute overflow-unset"
                className="sticky-table"
                itemProvider={this.filteredTaskboardRows}
                maxHeight={taskboardMaxHeight}
                renderRow={this.renderTaskboardRow}
                rowHeights={this.rowHeights}
                pageSize={pageSize}
                spacerWidth={0}
                showScroll={true}
            />
        );
    }

    private renderTaskboardNoData(): JSX.Element {
        return (
            <ZeroData
                className="padding-16"
                imagePath={getAssetLocation(this.context.pageContext, "ms.vss-work-web/common-backlog-content/Content/zerodata-no-results-found.svg")}
                imageAltText={Resources.ZeroDataNoResultsFound}
                secondaryText={Resources.ZeroDataNoResultsFound}
            />
        );
    }

    public render() {
        return (
            <Observer
                selectedIteration={{
                    observableValue: this.props.selectedIteration,
                    filter: (iteration: IIterationData | undefined) => {
                        if (iteration) {
                            this.taskboardService.setIteration(iteration);
                            return true;
                        }
                    }
                }}
                allTaskboardRows={this.taskboardService.getAllRows()}
                groupBy={{ observableValue: this.props.groupBy, filter: this.resetGroupByView }}
            >
                {(props: { allTaskboardRows: ITaskboardRow[]; groupBy: TaskboardGroupBy }) => {
                    if (!this.props.taskboardData || props.groupBy === TaskboardGroupBy.Loading) {
                        return <Spinner label={Resources.Loading} />;
                    }
                    if (this.isLoading) {
                        this.isLoading = false;
                        this.props.onMarkTTI();
                    }
                    if (props.allTaskboardRows.length) {
                        const columns = this.getTableColumns();
                        return (
                            <div className="flex-grow flex-column">
                                <InsertionMarker insertionMarkerPosition={this.insertionMarker} />
                                <TaskboardFilterBar
                                    className="padding-vertical-16"
                                    filterToggled={this.props.filterToggled}
                                    onFilterChangedCallback={this.updateTaskboard}
                                />
                                <Observer messages={this.messages}>
                                    {(props: { messages: string[] }) => {
                                        if (!props.messages.length) {
                                            return null;
                                        } else {
                                            return props.messages.map((message: string, index: number) => (
                                                <MessageCard
                                                    key={index}
                                                    className="flex-self-stretch taskboard-message-card"
                                                    severity={MessageCardSeverity.Warning}
                                                    onDismiss={() => {
                                                        this.messages.splice(index, 1);
                                                    }}
                                                >
                                                    {message}
                                                </MessageCard>
                                            ));
                                        }
                                    }}
                                </Observer>
                                <div className="full-height relative taskboard-content-wrapper" ref={this.scrollableContainer}>
                                    <Observer
                                        filteredRows={this.filteredTaskboardRows}
                                        newItem={{ observableValue: this.props.newItemType, filter: this.onNewItemTypeChanged }}
                                    >
                                        {(props: { filteredRows: ITaskboardRow[] }) => {
                                            if (props.filteredRows?.length > 0) {
                                                if (this.isCardSettingsOptimizationEnabled) {
                                                    this.taskboardService.updateBackgroundColorForFilteredTaskboardRows();
                                                }
                                                return this.renderTaskboard(columns);
                                            } else {
                                                return this.renderTaskboardNoData();
                                            }
                                        }}
                                    </Observer>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <TaskboardZeroData
                                teamSettings={this.props.teamSettings}
                                selectedIteration={this.props.selectedIteration}
                                backlogConfiguration={this.backlogConfiguration}
                            />
                        );
                    }
                }}
            </Observer>
        );
    }
}

interface ITaskboardHeaderProps {
    name: string;
    remainingWork: ObservableValue<number>;
}

const TaskboardHeader: React.FunctionComponent<ITaskboardHeaderProps> = ({ name, remainingWork }) => {
    return (
        <th className="text-left font-weight-normal">
            <div className="flex-row">
                {name}
                <Observer remainingWork={remainingWork}>
                    {(props: { remainingWork: number }) => {
                        if (props.remainingWork) {
                            return (
                                <Tooltip overflowOnly>
                                    <div className="font-weight-semibold margin-horizontal-8 margin text-ellipsis">
                                        {formatRemainingWorkForDisplay(props.remainingWork)}
                                    </div>
                                </Tooltip>
                            );
                        }
                        return null;
                    }}
                </Observer>
            </div>
        </th>
    );
};
